'use client';

import Script from 'next/script';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { theme, type Locale } from './theme';

declare global {
  interface Window {
    bookCallTurnstileCallback?: (token: string) => void;
    bookCallTurnstileExpired?: () => void;
    turnstile?: {
      reset: (widgetId?: string) => void;
    };
  }
}

const COPY = {
  pt: {
    eyebrow: 'Chamada de descoberta',
    title: 'Antes de marcar, confirma que es uma pessoa real.',
    intro:
      'Isto protege o calendario contra reservas falsas. Depois da verificacao, abrimos a pagina de marcacao com os horarios disponiveis.',
    name: 'O teu nome',
    email: 'Email profissional',
    business: 'Negocio ou website',
    note: 'O que gostarias de discutir?',
    notePlaceholder: 'Conta-nos em poucas linhas o objetivo do projeto, o tipo de negocio e o que queres melhorar.',
    submit: 'Verificar e escolher horario',
    loading: 'A verificar...',
    open: 'Abrir agenda',
    ready: 'Verificacao concluida. Ja podes escolher um horario.',
    redirecting: 'Verificacao concluida. A abrir agenda...',
    unavailable: 'A agenda ainda nao esta configurada. Envia email para info@locusly.pt.',
    failed: 'Nao conseguimos verificar o pedido. Tenta novamente.',
    antiSpamUnavailable:
      'Protecao anti-spam ainda nao configurada. Verifica as variaveis Turnstile.',
    tooFast: 'Espera alguns segundos e tenta novamente.',
    missingCaptcha: 'Confirma a verificacao antes de continuar.',
    back: 'Voltar ao site',
  },
  en: {
    eyebrow: 'Discovery call',
    title: 'Before booking, confirm you are a real person.',
    intro:
      'This protects the calendar from fake bookings. After verification, we will open the booking page with available times.',
    name: 'Your name',
    email: 'Work email',
    business: 'Business or website',
    note: 'What would you like to discuss?',
    notePlaceholder: 'Briefly describe the project goal, business type, and what you want to improve.',
    submit: 'Verify and choose a time',
    loading: 'Verifying...',
    open: 'Open scheduler',
    ready: 'Verification complete. You can now choose a time.',
    redirecting: 'Verification complete. Opening scheduler...',
    unavailable: 'The scheduler is not configured yet. Email info@locusly.pt.',
    failed: 'We could not verify this request. Try again.',
    antiSpamUnavailable: 'Anti-spam protection is not configured yet. Check the Turnstile variables.',
    tooFast: 'Wait a few seconds and try again.',
    missingCaptcha: 'Complete the verification before continuing.',
    back: 'Back to site',
  },
} satisfies Record<Locale, Record<string, string>>;

export function BookCallGate({
  locale,
  siteKey,
}: {
  locale: Locale;
  siteKey: string | null;
}) {
  const copy = COPY[locale];
  const startedAt = useMemo(() => Date.now(), []);
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    window.bookCallTurnstileCallback = (value: string) => setToken(value);
    window.bookCallTurnstileExpired = () => setToken('');
    return () => {
      delete window.bookCallTurnstileCallback;
      delete window.bookCallTurnstileExpired;
    };
  }, []);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!siteKey) {
      setStatus('error');
      setMessage(copy.unavailable);
      return;
    }
    if (!token) {
      setStatus('error');
      setMessage(copy.missingCaptcha);
      return;
    }

    const form = new FormData(e.currentTarget);
    setStatus('loading');
    setMessage(null);

    const response = await fetch('/api/book-call', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        startedAt,
        name: form.get('name'),
        email: form.get('email'),
        business: form.get('business'),
        note: form.get('note'),
        website: form.get('website'),
      }),
    });

    const data = (await response.json().catch(() => null)) as {
      bookingUrl?: string;
      error?: string;
    } | null;
    if (!response.ok || !data?.bookingUrl) {
      window.turnstile?.reset();
      setToken('');
      setStatus('error');
      if (data?.error === 'booking_unavailable') {
        setMessage(copy.unavailable);
      } else if (data?.error === 'not_configured') {
        setMessage(copy.antiSpamUnavailable);
      } else if (data?.error === 'too_fast') {
        setMessage(copy.tooFast);
      } else {
        setMessage(copy.failed);
      }
      return;
    }

    setStatus('ready');
    setMessage(copy.redirecting);
    window.location.assign(data.bookingUrl);
  }

  return (
    <>
      {siteKey ? (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js"
          strategy="afterInteractive"
        />
      ) : null}
      <div
        style={{
          minHeight: '100vh',
          background: theme.c.bg,
          color: theme.c.fg,
          fontFamily: theme.fonts.body,
          padding: '32px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <main
          style={{
            width: '100%',
            maxWidth: 720,
            background: theme.c.surface,
            border: `1px solid ${theme.c.border}`,
            borderRadius: theme.radii.xl,
            padding: 'clamp(28px, 6vw, 56px)',
            boxShadow: '0 30px 70px -45px rgba(27,23,20,0.35)',
          }}
        >
          <a
            href={`/${locale}`}
            className="lp-nav-link"
            style={{
              color: theme.c.fgMuted,
              fontFamily: theme.fonts.mono,
              fontSize: 12,
              letterSpacing: 1,
              textTransform: 'uppercase',
              textDecoration: 'none',
            }}
          >
            {copy.back}
          </a>

          <p
            style={{
              margin: '32px 0 12px',
              color: theme.c.accent,
              fontFamily: theme.fonts.mono,
              fontSize: 12,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
            }}
          >
            {copy.eyebrow}
          </p>
          <h1
            style={{
              margin: 0,
              fontFamily: theme.fonts.display,
              fontSize: 'clamp(38px, 7vw, 68px)',
              lineHeight: 1,
              letterSpacing: -1.5,
            }}
          >
            {copy.title}
          </h1>
          <p
            style={{
              margin: '22px 0 0',
              color: theme.c.fgMuted,
              fontSize: 17,
              lineHeight: 1.55,
              maxWidth: 620,
            }}
          >
            {copy.intro}
          </p>

          <form
            onSubmit={submit}
            data-locusly-event="lead_submitted"
            data-locusly-meta-source="book_call"
            style={{ marginTop: 34 }}
          >
            <input
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: -10000, width: 1, height: 1 }}
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 14,
              }}
              className="book-call-grid"
            >
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={labelStyle}>{copy.name}</span>
                <input className="lp-input" name="name" required minLength={2} style={inputStyle} />
              </label>
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={labelStyle}>{copy.email}</span>
                <input className="lp-input" name="email" required type="email" style={inputStyle} />
              </label>
            </div>

            <label style={{ display: 'grid', gap: 8, marginTop: 14 }}>
              <span style={labelStyle}>{copy.business}</span>
              <input className="lp-input" name="business" required minLength={2} style={inputStyle} />
            </label>

            <label style={{ display: 'grid', gap: 8, marginTop: 14 }}>
              <span style={labelStyle}>{copy.note}</span>
              <textarea
                className="lp-input"
                name="note"
                required
                minLength={20}
                maxLength={1200}
                rows={5}
                placeholder={copy.notePlaceholder}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
              />
            </label>

            <div style={{ marginTop: 18, minHeight: 65 }}>
              {siteKey ? (
                <div
                  className="cf-turnstile"
                  data-sitekey={siteKey}
                  data-callback="bookCallTurnstileCallback"
                  data-expired-callback="bookCallTurnstileExpired"
                />
              ) : null}
            </div>

            {message ? (
              <p
                role="status"
                style={{
                  margin: '8px 0 0',
                  color: status === 'ready' ? theme.c.success : theme.c.danger,
                  fontSize: 14,
                }}
              >
                {message}
              </p>
            ) : null}

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 24 }}>
              <button
                type="submit"
                disabled={status === 'loading' || status === 'ready'}
                className="lp-btn-primary"
                style={{
                  border: 0,
                  cursor: status === 'loading' || status === 'ready' ? 'default' : 'pointer',
                  background: theme.c.accent,
                  color: theme.c.accentFg,
                  fontFamily: theme.fonts.body,
                  fontSize: 15,
                  fontWeight: 500,
                  padding: '14px 24px',
                  borderRadius: theme.radii.pill,
                  opacity: status === 'loading' || status === 'ready' ? 0.72 : 1,
                }}
              >
                {status === 'loading' || status === 'ready' ? copy.loading : copy.submit}
              </button>
            </div>
          </form>
        </main>
      </div>
      <style>{`
        @media (max-width: 680px) {
          .book-call-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

const labelStyle = {
  color: theme.c.fgMuted,
  fontFamily: theme.fonts.mono,
  fontSize: 11,
  letterSpacing: 1.1,
  textTransform: 'uppercase',
} as const;

const inputStyle = {
  width: '100%',
  border: `1px solid ${theme.c.borderStrong}`,
  borderRadius: theme.radii.md,
  background: theme.c.bg,
  color: theme.c.fg,
  fontFamily: theme.fonts.body,
  fontSize: 15,
  padding: '13px 14px',
} as const;
